import { createRouter, createWebHistory } from "vue-router";
import { getToken } from "rdep-ecom-sdk";
import LoginForm from "../components/LoginForm.vue";
import NewRegister from "../components/NewRegister.vue";
import ProfileView from "../components/account-profile/ProfileView.vue";
import HomeEntryView from "../components/pages/HomeEntryView.vue";
import MyDashboardView from "../components/account-profile/MyDashboardView.vue";
import ShopView from "../components/pages/ShopView.vue";
import ProductDetailView from "../components/pages/ProductDetailView.vue";
import CartView from "../components/pages/CartView.vue";
import PlaceOrderView from "../components/pages/PlaceOrderView.vue";
import PaymentOptionsView from "../components/pages/PaymentOptionsView.vue";
import DigilockerComplete from "../components/DigilockerComplete.vue";
import PaymentStatusView from "../components/pages/PaymentStatusView.vue";
import OrderStatus from "../components/account-profile/OrderStatus.vue";
import DeliveryStatus from "../components/account-profile/DeliveryStatus.vue";
import PrivacyPolicyView from "../components/footer-policies/PrivacyPolicyView.vue";
import TermsOfUseView from "../components/footer-policies/TermsOfUseView.vue";
import ReturnPolicyView from "../components/footer-policies/ReturnPolicyView.vue";
import FaqView from "../components/footer-policies/FaqView.vue";
import SupportView from "../components/footer-policies/SupportView.vue";
import CookiePolicyView from "../components/footer-policies/CookiePolicyView.vue";
import SuperPromo from "../components/pages/SuperPromo.vue";

const routes = [
  {
    path: "/login",
    name: "login",
    component: LoginForm,
  },
  {
    path: "/register",
    name: "register",
    component: NewRegister,
  },
  {
    path: "/profile",
    name: "profile",
    component: ProfileView,
    meta: { requiresAuth: true },
  },
  {
    path: "/dashboard",
    name: "dashboard",
    component: MyDashboardView,
    meta: { requiresAuth: true },
  },
  {
    path: "/",
    name: "home",
    component: HomeEntryView,
  },
  {
    path: "/shop",
    name: "shop",
    component: ShopView,
  },
  {
    path: "/super-promo",
    name: "super-promo",
    component: SuperPromo,
  },
  {
    path: "/shop/product/:productId",
    name: "product-detail",
    component: ProductDetailView,
  },
  {
    path: "/cart",
    name: "cart",
    component: CartView,
  },
  {
    path: "/place-order",
    name: "place-order",
    component: PlaceOrderView,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: "/payment-options",
    name: "payment-options",
    component: PaymentOptionsView,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: "/order-status",
    name: "order-status",
    component: OrderStatus,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: "/order-status/:orderId/delivery",
    name: "delivery-status",
    component: DeliveryStatus,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: "/privacy-policy",
    name: "privacy-policy",
    component: PrivacyPolicyView,
  },
  {
    path: "/cookie-policy",
    name: "cookie-policy",
    component: CookiePolicyView,
  },
  {
    path: "/terms-of-use",
    name: "terms-of-use",
    component: TermsOfUseView,
  },
  {
    path: "/faq",
    name: "faq",
    component: FaqView,
  },
  {
    path: "/support",
    name: "support",
    component: SupportView,
  },
  {
    path: "/return-policy",
    name: "return-policy",
    component: ReturnPolicyView,
  },
  {
    path: "/payment-status/:uid?",
    name: "payment-status",
    component: PaymentStatusView,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: "/digilocker",
    name: "digilocker",
    component: DigilockerComplete,
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior() {
    return {
      top: 0,
      left: 0,
      behavior: "auto",
    };
  },
});

function hasAuthToken() {
  const sdkToken = String(getToken() || "").trim();
  if (sdkToken) {
    return true;
  }

  const savedAuthRaw = window.localStorage.getItem("auth.currentUser");
  if (!savedAuthRaw) {
    return false;
  }

  try {
    const savedAuth = JSON.parse(savedAuthRaw);
    return Boolean(String(savedAuth?.token || "").trim());
  } catch (error) {
    return false;
  }
}

function extractCallbackUid(query = {}) {
  const sanitizeUid = (value) => {
    const wrappers = new Set(['"', "'", "`", "(", ")", "[", "]"]);
    let next = String(value || "").trim();

    while (next.length && wrappers.has(next.charAt(0))) {
      next = next.slice(1).trim();
    }

    while (next.length && wrappers.has(next.charAt(next.length - 1))) {
      next = next.slice(0, -1).trim();
    }

    return next;
  };

  const extractUidFromUrlLike = (value) => {
    const raw = String(value || "").trim();
    if (!raw) return "";

    const cleaned = raw
      .replace(/^[\s"'`(]+/, "")
      .replace(/[\s"'`)]+$/, "")
      .trim();

    const candidates = [cleaned];
    try {
      candidates.push(decodeURIComponent(cleaned));
    } catch (error) {
      // ignore decode failures
    }

    for (const candidate of candidates) {
      try {
        const parsed = new URL(candidate);
        const uidFromUrl = sanitizeUid(
          parsed.searchParams.get("uid") ||
            parsed.searchParams.get("UID") ||
            parsed.searchParams.get("paymentUid") ||
            parsed.searchParams.get("paymentUID") ||
            "",
        );
        if (uidFromUrl) return uidFromUrl;
      } catch (error) {
        // Fallback for non-URL values that still contain query segments.
        const qIndex = candidate.indexOf("?");
        if (qIndex < 0) {
          continue;
        }

        const params = new URLSearchParams(candidate.slice(qIndex + 1));
        const uidFromParams = sanitizeUid(
          params.get("uid") ||
            params.get("UID") ||
            params.get("paymentUid") ||
            params.get("paymentUID") ||
            "",
        );
        if (uidFromParams) return uidFromParams;
      }
    }

    return "";
  };

  const source = query && typeof query === "object" ? query : {};
  let uid = sanitizeUid(
    source.uid || source.UID || source.paymentUid || source.paymentUID || "",
  );

  if (uid) {
    return uid;
  }

  uid = extractUidFromUrlLike(source.returnUrl || source.returnURL || "");
  if (uid) {
    return uid;
  }

  const rawOrderId = String(
    source.orderId || source.orderID || source.order_id || "",
  ).trim();
  const embeddedIndex = rawOrderId.indexOf("?");

  if (embeddedIndex < 0) {
    return "";
  }

  const embeddedQuery = rawOrderId.slice(embeddedIndex + 1).trim();
  const params = new URLSearchParams(embeddedQuery);

  uid = sanitizeUid(
    params.get("uid") ||
      params.get("UID") ||
      params.get("paymentUid") ||
      params.get("paymentUID") ||
      "",
  );

  if (uid) {
    return uid;
  }

  uid = extractUidFromUrlLike(
    params.get("returnUrl") || params.get("returnURL") || "",
  );

  return uid;
}

router.beforeEach((to) => {
  const callbackUid = extractCallbackUid(to.query || {});
  const paymentStatusPathUid = String(to.params?.uid || "").trim();

  const isPaymentStatusCallbackRoute =
    to.name === "payment-status" &&
    Boolean(callbackUid || paymentStatusPathUid);

  if (
    to.meta?.requiresAuth &&
    !hasAuthToken() &&
    !isPaymentStatusCallbackRoute
  ) {
    return {
      name: "login",
      query: {
        redirect: to.fullPath,
      },
    };
  }

  return true;
});

export default router;
